import { useEffect } from 'react';

import { getMemberId } from '@trello/authentication';
import { Entitlements } from '@trello/entitlements';
import { deepEqual } from '@trello/objects';
import { PersistentSharedState, useSharedState } from '@trello/shared-state';

import type { MemberEnterpriseDataFragment } from './MemberEnterprisesDataFragment.generated';
import { useMemberEnterpriseDataFragment } from './MemberEnterprisesDataFragment.generated';

export interface MemberEnterpriseDataSharedState {
  idEnterprise: string | undefined;
  idEnterprises: string[];
  inEnterprise: boolean | undefined;
  inRealEnterprise: boolean | undefined;
  hasEnterpriseDomain: boolean | undefined;
}

const enterpriseDataSharedState: MemberEnterpriseDataSharedState = {
  idEnterprise: undefined,
  idEnterprises: [],
  inEnterprise: undefined,
  inRealEnterprise: undefined,
  hasEnterpriseDomain: undefined,
};

const makeEnterpriseData = (
  memberData: MemberEnterpriseDataFragment | undefined,
): MemberEnterpriseDataSharedState => {
  return {
    idEnterprise: memberData?.idEnterprise || undefined,
    idEnterprises: memberData?.enterprises?.map(({ id }) => id) || [],
    inEnterprise: (memberData?.enterprises || []).length > 0,
    inRealEnterprise: memberData?.enterprises.some((enterprise) =>
      Entitlements.isEnterprise(enterprise.offering),
    ),
    hasEnterpriseDomain: memberData?.hasEnterpriseDomain || false,
  };
};

// We version the key so that stale web versions do not try to write values
// with an old data shape to local storage.
// Please update this value when MemberEnterpriseDataSharedState type definition changes.
const version = 'f5e89a12fa7aa1a021f6fc50c7014c9bd4db058d';

const memberIdEnterprisesSharedState =
  new PersistentSharedState<MemberEnterpriseDataSharedState>(
    enterpriseDataSharedState,
    {
      storageKey: () =>
        `memberEnterpriseData-${getMemberId() || 'anonymous'}-${version}`,
    },
  );

export const useCachedMemberEnterpriseData: (
  trelloMemberId: string,
) => MemberEnterpriseDataSharedState = (trelloMemberId) => {
  const { data: memberData, complete: isMemberDataLoaded } =
    useMemberEnterpriseDataFragment({
      from: { id: trelloMemberId },
    });

  const [enterprisesState, setMemberEnterpriseData] = useSharedState(
    memberIdEnterprisesSharedState,
  );

  useEffect(() => {
    if (!isMemberDataLoaded) {
      return;
    }

    const fetchedEnterpriseData = makeEnterpriseData(memberData);
    if (!deepEqual(enterprisesState, fetchedEnterpriseData)) {
      setMemberEnterpriseData(fetchedEnterpriseData);
    }
  }, [
    enterprisesState,
    isMemberDataLoaded,
    memberData,
    setMemberEnterpriseData,
  ]);

  if (!isMemberDataLoaded) {
    return enterprisesState;
  }

  return makeEnterpriseData(memberData);
};
